/**
 * Onboarding target registry.
 *
 * These ids are a stable contract between the editor UI and the onboarding
 * tours. They are deliberately kept SEPARATE from `data-testid`.
 *
 * Why separate: test ids are refactoring debris — renaming one breaks exactly
 * one test, loudly, in CI. Onboarding anchors have no such feedback loop, so
 * reusing test ids would turn every rename into a silent production
 * regression. That is precisely how the previous onboarding system rotted:
 * the editor was redesigned, ids drifted, and six tour steps quietly stopped
 * resolving for months.
 *
 * Conventions:
 *  - Prefer anchoring a persistent REGION (a column, a panel) over a transient
 *    control. Regions survive redesigns; buttons move.
 *  - Every entry here must be applied somewhere in production source, and must
 *    resolve when its surface is mounted. Both are enforced by tests in
 *    `__tests__/`, so an unused or unrendered entry fails CI.
 *
 * Adding a target:
 *  1. add the entry below,
 *  2. apply it — either spread `onboardingTarget(...)` onto a DOM element, or
 *     pass the `onboardingId` prop on components that accept one,
 *  3. add it to the relevant render test.
 */

export const ONBOARDING_ATTRIBUTE = "data-onboarding-id";

export const OnboardingTarget = {
  // --- Shared across training types ---
  /** The right-hand inspector panel. */
  EditorPropertiesColumn: "editor-properties-column",
  /** The actionbar Test / Resolve Errors split button. */
  ActionbarTestButton: "actionbar-test-button",
  /** The actionbar translate button — opens the localization dialog. */
  ActionbarTranslate: "actionbar-translate",

  // --- 360 ---
  /** Column listing the training's takes ("bubbles") in running order. */
  Editor360TakesColumn: "editor-360-takes-column",
  /** Footer CTA that opens the media picker and creates takes. */
  Editor360AddTake: "editor-360-add-take",
  /** Column listing the selected take's interactions. */
  Editor360InteractionColumn: "editor-360-interaction-column",
  /** Inline CTA that opens the interaction picker — the core authoring action. */
  Editor360AddInteraction: "editor-360-add-interaction",
  /** The main Save button in the training actionbar. */
  Editor360SaveButton: "editor-360-save-button",
  /** Sidebar button that opens the publish dialog. */
  Editor360PublishButton: "editor-360-publish-button",
  /** The 360 scene viewport where interactions are dragged into place. */
  Editor360Viewport: "editor-360-viewport",

  // --- 3D ---
  /** The hero that replaces the whole editor body while a 3D project is empty. */
  Editor3DEmptyHero: "editor-3d-empty-hero",
  /** Tab rail: swaps the middle column between Scene, Actors and Props. */
  Editor3DTabRail: "editor-3d-tab-rail",
  /** Ordered list of takes. */
  Editor3DStoryboard: "editor-3d-storyboard",
  /** Outline of the selected take. */
  Editor3DScenarioBuilder: "editor-3d-scenario-builder",
  /** The Leaflet floorplan where takes, actors and props are positioned. */
  Editor3DViewport: "editor-3d-viewport",

  // --- Dashboard ---
  /**
   * The org wide training list on the dashboard.
   *
   * Roughly 490px tall on every screen, because the page size is fixed at five
   * rows (ProjectsSlice.tsx:19), so on a short window there is no room for a
   * tooltip either above or below it. The step that uses this anchor therefore
   * asks for "top-start" with a zero offset, which lets the panel sit flush
   * under the list on a tall screen and ride up over it on a short one.
   * See docs/DASHBOARD_ONBOARDING_PLAN.md 9.4.
   */
  DashboardProjectsTable: "dashboard-projects-table",
  /**
   * The rows of the training list, i.e. the table body.
   *
   * Anchored on the rows rather than the column headers, because the step that
   * uses it says "click a row to open it" and the spotlight has to land on the
   * thing being described. It is also why the tour is staged: an organisation
   * with no trainings has no rows, so that step is withheld entirely rather
   * than pointed at an empty body. See tours/dashboardTours.ts.
   */
  DashboardTrainingRows: "dashboard-training-rows",
  /** Header CTA that opens the new training wizard. */
  DashboardNewTraining: "dashboard-new-training",
  /** Sidebar entry that opens the shared asset library. */
  DashboardFileExplorer: "dashboard-file-explorer",
} as const;

export type OnboardingTargetId =
  (typeof OnboardingTarget)[keyof typeof OnboardingTarget];

export const ONBOARDING_TARGET_IDS = Object.values(
  OnboardingTarget
) as OnboardingTargetId[];

export interface OnboardingTargetAttributes {
  readonly "data-onboarding-id": OnboardingTargetId;
}

/**
 * Spread onto a DOM element to anchor it:
 *   <div {...onboardingTarget(OnboardingTarget.Editor360Viewport)} />
 *
 * Components that do not spread arbitrary props expose an `onboardingId` prop
 * instead (see `EditorColumn.tsx`).
 */
export const onboardingTarget = (
  id: OnboardingTargetId
): OnboardingTargetAttributes => ({ "data-onboarding-id": id });

/** CSS selector for a target — the only way tours should reference the DOM. */
export const onboardingSelector = (id: OnboardingTargetId): string =>
  `[${ONBOARDING_ATTRIBUTE}="${id}"]`;

/** Resolve a target, or null when its surface is not mounted. */
export const resolveOnboardingTarget = (
  id: OnboardingTargetId,
  root: ParentNode = document
): Element | null => root.querySelector(onboardingSelector(id));
